import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Lock,
  LogOut,
  Upload,
  Trash2,
  RefreshCw,
  Image as ImageIcon,
  Users,
  Mail,
  DollarSign,
  Plus,
  Edit2,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Search,
  Eye,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PRESET_IMAGE_SLOTS = [
  { id: 'logo.png', label: 'Main Website Logo', category: 'Logo', desc: 'Used in header, navbar, and footer' },
  { id: 'dileep-raj.jpg', label: 'Founder & CEO Photo (Dileep Raj)', category: 'Team Member', desc: 'Displayed on Leadership section' },
  { id: 'team1.jpg', label: 'Core Team Member 1 Photo', category: 'Team Member', desc: 'Displayed on Team page' },
  { id: 'team2.jpg', label: 'Core Team Member 2 Photo', category: 'Team Member', desc: 'Displayed on Team page' },
  { id: 'team3.jpg', label: 'Core Team Member 3 Photo', category: 'Team Member', desc: 'Displayed on Team page' },
  { id: 'qr-code.png', label: 'Donation QR Code (Default)', category: 'Donation QR', desc: 'Displayed on Donate page and modal' },
  { id: 'qr_code.jpeg', label: 'Donation QR Code (Alternative)', category: 'Donation QR', desc: 'Alternative QR code asset' },
  { id: 'gallery1.jpeg', label: 'Gallery / Slider Image 1', category: 'Gallery / Hero Slider', desc: 'Career Counseling Session' },
  { id: 'gallery2.jpeg', label: 'Gallery / Slider Image 2', category: 'Gallery / Hero Slider', desc: 'Workshop Program' },
  { id: 'gallery3.jpeg', label: 'Gallery / Slider Image 3', category: 'Gallery / Hero Slider', desc: 'Women Empowerment' },
  { id: 'gallery4.jpeg', label: 'Gallery / Slider Image 4', category: 'Gallery / Hero Slider', desc: 'Student Counseling' },
  { id: 'gallery5.jpeg', label: 'Gallery / Slider Image 5', category: 'Gallery / Hero Slider', desc: 'Community Awareness' },
  { id: 'gallery6.jpeg', label: 'Gallery / Slider Image 6', category: 'Gallery / Hero Slider', desc: 'Educational Program' },
];

const Admin = () => {
  // Auth state
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Active Tab
  const [activeTab, setActiveTab] = useState('images'); // 'images', 'team', 'contacts', 'donations'

  // Image Manager State
  const [images, setImages] = useState([]);
  const [imageCategoryFilter, setImageCategoryFilter] = useState('All');
  const [imagesLoading, setImagesLoading] = useState(false);
  const [uploadTargetName, setUploadTargetName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [previewModalImg, setPreviewModalImg] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  // Team State
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalLoading, setTeamModalLoading] = useState(false);
  const [teamModalError, setTeamModalError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    name: '',
    position: '',
    education: '',
    experience: '',
    description: '',
    image: '/images/gallery1.jpeg'
  });

  // Contacts & Donations State
  const [contacts, setContacts] = useState([]);
  const [donations, setDonations] = useState([]);

  // Headers helper
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Toast auto-clear
  const showStatus = (type, text) => {
    setStatusMessage({ type, text });
    setTimeout(() => {
      setStatusMessage({ type: '', text: '' });
    }, 5000);
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data && res.data.token) {
        localStorage.setItem('admin_token', res.data.token);
        setToken(res.data.token);
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Invalid username or password');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
  };

  // Fetch Images
  const fetchImages = async () => {
    if (!token) return;
    setImagesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/images?t=${Date.now()}`, getAuthHeaders());
      setImages(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      showStatus('error', 'Failed to load images from server');
    } finally {
      setImagesLoading(false);
    }
  };

  // Fetch Team
  const fetchTeam = async () => {
    setTeamLoading(true);
    try {
      const res = await axios.get(`${API}/team?t=${Date.now()}`);
      setTeamMembers(res.data);
    } catch (err) {
      showStatus('error', 'Failed to load team members');
    } finally {
      setTeamLoading(false);
    }
  };

  // Fetch Inquiries
  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, getAuthHeaders());
      setContacts(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
    }
  };

  // Fetch Donations
  const fetchDonations = async () => {
    try {
      const res = await axios.get(`${API}/admin/donations`, getAuthHeaders());
      setDonations(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
    }
  };

  useEffect(() => {
    if (token) {
      fetchImages();
      fetchTeam();
      fetchContacts();
      fetchDonations();
    }
  }, [token]);

  // Handle File Selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      setUploadPreview(URL.createObjectURL(file));
    }
  };

  // Handle Image Upload / Replace
  const handleUploadImage = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      showStatus('error', 'Please select an image file to upload.');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadTargetName) {
      formData.append('target_name', uploadTargetName);
    }

    try {
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      showStatus('success', res.data.message || 'Image uploaded & cache purged successfully!');
      setUploadFile(null);
      setUploadPreview('');
      setUploadTargetName('');
      // Refresh image list with a short delay to allow file system sync
      setTimeout(fetchImages, 500);
    } catch (err) {
      showStatus('error', err.response?.data?.detail || 'Error uploading image');
    } finally {
      setUploadLoading(false);
    }
  };

  // Handle Quick Replace
  const handleQuickReplace = (filename) => {
    setUploadTargetName(filename);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    showStatus('info', `Selected "${filename}" for replacement. Now choose your new photo above and click Upload.`);
  };

  // Handle Delete Image
  const handleDeleteImage = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/delete-image/${encodeURIComponent(filename)}`, getAuthHeaders());
      showStatus('success', `Deleted "${filename}" successfully!`);
      fetchImages();
    } catch (err) {
      showStatus('error', err.response?.data?.detail || 'Failed to delete image');
    }
  };

  // Copy Image URL to Clipboard
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopySuccess(url);
    setTimeout(() => setCopySuccess(''), 2500);
  };

  // Team Form Handlers
  const handleOpenAddTeam = () => {
    setEditingMember(null);
    setTeamModalError('');
    setMemberForm({
      name: '',
      position: '',
      education: '',
      experience: '',
      description: '',
      image: '/images/gallery1.jpeg'
    });
    setShowTeamModal(true);
  };

  const handleOpenEditTeam = (member) => {
    setEditingMember(member);
    setTeamModalError('');
    setMemberForm({
      name: member.name,
      position: member.position,
      education: member.education || '',
      experience: member.experience || '',
      description: member.description || '',
      image: member.image || '/images/gallery1.jpeg'
    });
    setShowTeamModal(true);
  };

  const handleSaveTeamMember = async (e) => {
    e.preventDefault();
    setTeamModalLoading(true);
    setTeamModalError('');

    const payload = {
      name: memberForm.name.trim(),
      position: memberForm.position.trim(),
      education: memberForm.education ? memberForm.education.trim() : '',
      experience: memberForm.experience ? memberForm.experience.trim() : '',
      description: memberForm.description ? memberForm.description.trim() : '',
      image: memberForm.image ? memberForm.image.trim() : '/images/logo.png'
    };

    try {
      if (editingMember) {
        await axios.put(`${API}/admin/team/${editingMember.id}`, payload, getAuthHeaders());
        showStatus('success', `Updated member "${payload.name}" successfully!`);
      } else {
        await axios.post(`${API}/admin/team`, payload, getAuthHeaders());
        showStatus('success', `Added new member "${payload.name}" successfully!`);
      }
      setShowTeamModal(false);
      fetchTeam();
    } catch (err) {
      const errDetail = err.response?.data?.detail || err.message || 'Failed to save team member';
      setTeamModalError(typeof errDetail === 'string' ? errDetail : JSON.stringify(errDetail));
      showStatus('error', 'Error saving team member. Check the form fields.');
    } finally {
      setTeamModalLoading(false);
    }
  };

  const handleDeleteTeamMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove team member "${memberName}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/team/${memberId}`, getAuthHeaders());
      showStatus('success', `Removed "${memberName}" from team.`);
      fetchTeam();
    } catch (err) {
      showStatus('error', err.response?.data?.detail || 'Failed to delete member');
    }
  };

  // Filtered Images
  const filteredImages = images.filter((img) => {
    if (imageCategoryFilter === 'All') return true;
    return img.category === imageCategoryFilter;
  });

  // Render Login Form if unauthenticated
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 text-purple-600 mb-4 shadow-inner">
              <Lock size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">R.P. Trust Admin</h2>
            <p className="text-sm text-gray-500 mt-1">Sign in to manage images, team & content</p>
          </div>

          {authError && (
            <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-center">
              <AlertCircle size={18} className="mr-2 flex-shrink-0" />
              <span>{authError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={authLoading}
              className="w-full mt-6 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center"
            >
              {authLoading ? (
                <RefreshCw size={20} className="animate-spin mr-2" />
              ) : (
                'Sign In to Dashboard'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Default Credentials: <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700 font-mono">admin</code> / <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700 font-mono">admin@123</code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/images/logo.png" alt="Logo" className="w-9 h-9 rounded-full border border-purple-200" />
            <div>
              <h1 className="font-bold text-gray-900 text-lg leading-tight">Admin Dashboard</h1>
              <p className="text-xs text-purple-600 font-medium">R.P. Educational & Welfare Trust</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center text-xs font-medium text-gray-600 hover:text-purple-600 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-md transition-colors"
            >
              <ExternalLink size={14} className="mr-1.5" />
              View Live Website
            </a>
            <button
              onClick={handleLogout}
              className="inline-flex items-center text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md transition-colors"
            >
              <LogOut size={14} className="mr-1.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Status Alerts */}
      {statusMessage.text && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 w-full">
          <div
            className={`p-4 rounded-lg flex items-center justify-between ${
              statusMessage.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : statusMessage.type === 'info'
                ? 'bg-blue-50 text-blue-800 border border-blue-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}
          >
            <div className="flex items-center">
              {statusMessage.type === 'error' ? (
                <AlertCircle size={20} className="mr-2 text-red-600 flex-shrink-0" />
              ) : (
                <CheckCircle size={20} className="mr-2 text-green-600 flex-shrink-0" />
              )}
              <span className="font-medium text-sm">{statusMessage.text}</span>
            </div>
            <button onClick={() => setStatusMessage({ type: '', text: '' })}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full flex-grow">
        {/* Navigation Tabs */}
        <div className="flex space-x-2 border-b border-gray-200 pb-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('images')}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center transition-all ${
              activeTab === 'images'
                ? 'bg-purple-600 text-white shadow'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ImageIcon size={18} className="mr-2" />
            Image Manager ({images.length})
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center transition-all ${
              activeTab === 'team'
                ? 'bg-purple-600 text-white shadow'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Users size={18} className="mr-2" />
            Team Members ({teamMembers.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center transition-all ${
              activeTab === 'contacts'
                ? 'bg-purple-600 text-white shadow'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Mail size={18} className="mr-2" />
            Contact Messages ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('donations')}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center transition-all ${
              activeTab === 'donations'
                ? 'bg-purple-600 text-white shadow'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <DollarSign size={18} className="mr-2" />
            Donation Leads ({donations.length})
          </button>
        </div>

        {/* TAB 1: IMAGE MANAGER */}
        {activeTab === 'images' && (
          <div className="space-y-6">
            {/* Upload & Replace Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                <Upload className="mr-2 text-purple-600" size={20} />
                Upload New Image or Replace Existing Asset
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Choose a specific slot to replace instantly (e.g. Logo, QR code, Slider photo) or enter a custom name to add new photos. LiteSpeed cache is automatically purged on upload.
              </p>

              <form onSubmit={handleUploadImage} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Step 1: Target Slot / Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Target Slot / Preset:
                  </label>
                  <select
                    value={uploadTargetName}
                    onChange={(e) => setUploadTargetName(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    <option value="">-- Upload as New File (Auto-name) --</option>
                    <optgroup label="Website Preset Replacements">
                      {PRESET_IMAGE_SLOTS.map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {slot.label} ({slot.id})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <div className="pt-2">
                    <label className="block text-xs text-gray-500 font-medium">Or type custom filename:</label>
                    <input
                      type="text"
                      placeholder="e.g. event_2026.jpg"
                      value={uploadTargetName}
                      onChange={(e) => setUploadTargetName(e.target.value)}
                      className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Step 2: Choose File */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Select Image File:
                  </label>
                  <div className="border-2 border-dashed border-gray-300 hover:border-purple-400 rounded-lg p-4 text-center cursor-pointer transition-colors relative bg-gray-50">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    {uploadPreview ? (
                      <div className="flex flex-col items-center">
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="h-20 w-auto object-contain rounded mb-1"
                        />
                        <span className="text-xs text-green-700 font-medium truncate max-w-full">
                          {uploadFile?.name}
                        </span>
                      </div>
                    ) : (
                      <div className="py-2">
                        <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                        <span className="text-xs text-gray-600 font-medium">Click or Drag Image Here</span>
                        <p className="text-[11px] text-gray-400">PNG, JPG, JPEG, WEBP, SVG</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3: Action Button */}
                <div className="flex flex-col justify-end">
                  <button
                    type="submit"
                    disabled={uploadLoading || !uploadFile}
                    className={`w-full py-3 px-4 rounded-lg font-semibold text-white shadow-md flex items-center justify-center transition-all ${
                      uploadLoading || !uploadFile
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700 hover:shadow-lg'
                    }`}
                  >
                    {uploadLoading ? (
                      <>
                        <RefreshCw size={18} className="animate-spin mr-2" />
                        Uploading & Purging Cache...
                      </>
                    ) : (
                      <>
                        <Upload size={18} className="mr-2" />
                        {uploadTargetName ? `Replace "${uploadTargetName}"` : 'Upload Image'}
                      </>
                    )}
                  </button>
                  {uploadTargetName && (
                    <button
                      type="button"
                      onClick={() => setUploadTargetName('')}
                      className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline text-center"
                    >
                      Clear Target Slot (Upload as New)
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Presets Quick-Reference Cards */}
            <div className="bg-purple-50 rounded-xl border border-purple-100 p-5">
              <h3 className="text-sm font-bold text-purple-900 mb-3 uppercase tracking-wider">
                ⚡ Quick Replace Presets for Key Website Assets
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {PRESET_IMAGE_SLOTS.map((slot) => {
                  const currentImg = images.find((i) => i.filename === slot.id);
                  return (
                    <div
                      key={slot.id}
                      className="bg-white p-3 rounded-lg border border-purple-200 shadow-sm flex items-center space-x-3 justify-between"
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        {currentImg ? (
                          <img
                            src={`${currentImg.url}?t=${Date.now()}`}
                            alt={slot.label}
                            className="w-10 h-10 rounded object-cover border border-gray-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                            None
                          </div>
                        )}
                        <div className="truncate">
                          <p className="text-xs font-bold text-gray-900 truncate">{slot.label}</p>
                          <p className="text-[11px] text-gray-500 font-mono truncate">{slot.id}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleQuickReplace(slot.id)}
                        className="px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 rounded flex-shrink-0 transition-colors"
                      >
                        Replace
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Filter Tabs & Image Grid */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-gray-200 gap-4">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-bold text-gray-900">All Live Website Media</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                    {filteredImages.length} items
                  </span>
                </div>

                {/* Categories */}
                <div className="flex flex-wrap gap-1.5">
                  {['All', 'Logo', 'Gallery / Hero Slider', 'Team Member', 'Donation QR', 'General'].map(
                    (cat) => (
                      <button
                        key={cat}
                        onClick={() => setImageCategoryFilter(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          imageCategoryFilter === cat
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat}
                      </button>
                    )
                  )}
                  <button
                    onClick={fetchImages}
                    title="Refresh image list"
                    className="p-1 text-gray-500 hover:text-purple-600 rounded"
                  >
                    <RefreshCw size={16} className={imagesLoading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Grid of Images */}
              {imagesLoading ? (
                <div className="py-12 flex justify-center items-center">
                  <RefreshCw className="animate-spin text-purple-600 mr-2" size={24} />
                  <span className="text-gray-500 text-sm">Loading media files...</span>
                </div>
              ) : filteredImages.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <ImageIcon size={36} className="mx-auto text-gray-300 mb-2" />
                  <p>No images found for category "{imageCategoryFilter}"</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
                  {filteredImages.map((img) => (
                    <div
                      key={img.filename}
                      className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow bg-white flex flex-col justify-between"
                    >
                      {/* Image Thumbnail with Overlay */}
                      <div className="relative group bg-gray-100 h-44 flex items-center justify-center overflow-hidden">
                        <img
                          src={`${img.url}?t=${Date.now()}`}
                          alt={img.filename}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300x200?text=Image+Load+Error';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center space-x-2 opacity-0 group-hover:opacity-100">
                          <button
                            onClick={() => setPreviewModalImg(img.url)}
                            className="bg-white text-gray-800 p-2 rounded-full hover:bg-gray-100 shadow"
                            title="Preview Full Size"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleQuickReplace(img.filename)}
                            className="bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700 shadow"
                            title="Replace this image"
                          >
                            <Upload size={16} />
                          </button>
                        </div>
                        <span className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-[10px] font-semibold px-2 py-0.5 rounded">
                          {img.category}
                        </span>
                      </div>

                      {/* Details & Actions */}
                      <div className="p-3">
                        <p className="font-mono text-xs font-bold text-gray-900 truncate mb-1" title={img.filename}>
                          {img.filename}
                        </p>
                        <div className="flex justify-between text-[11px] text-gray-500 mb-3">
                          <span>{img.size_kb} KB</span>
                          <span>{img.last_modified}</span>
                        </div>

                        <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => handleCopyUrl(img.url)}
                            className={`flex-1 py-1.5 px-2 rounded text-xs font-medium flex items-center justify-center border transition-colors ${
                              copySuccess === img.url
                                ? 'bg-green-50 border-green-300 text-green-700'
                                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {copySuccess === img.url ? (
                              <>
                                <CheckCircle size={12} className="mr-1" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy size={12} className="mr-1" />
                                Copy URL
                              </>
                            )}
                          </button>

                          <button
                            onClick={() => handleQuickReplace(img.filename)}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded border border-purple-200"
                            title="Replace Image"
                          >
                            <RefreshCw size={14} />
                          </button>

                          <button
                            onClick={() => handleDeleteImage(img.filename)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded border border-red-200"
                            title="Delete Image"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: TEAM MEMBER MANAGEMENT */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-gray-200 gap-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Manage Team & Leadership</h2>
                  <p className="text-sm text-gray-500">
                    Add, edit, or remove team members displayed on the public Team page and Home page.
                  </p>
                </div>
                <button
                  onClick={handleOpenAddTeam}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center shadow"
                >
                  <Plus size={16} className="mr-1.5" />
                  Add Team Member
                </button>
              </div>

              {/* Members List */}
              {teamLoading ? (
                <div className="py-12 flex justify-center items-center">
                  <RefreshCw className="animate-spin text-purple-600 mr-2" size={24} />
                  <span className="text-gray-500 text-sm">Loading team members...</span>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <Users size={36} className="mx-auto text-gray-300 mb-2" />
                  <p>No team members found in database.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm flex flex-col justify-between hover:border-purple-300 transition-colors"
                    >
                      <div className="flex items-start space-x-4">
                        <img
                          src={`${member.image}?t=${Date.now()}`}
                          alt={member.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-purple-200 flex-shrink-0"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/100?text=Photo';
                          }}
                        />
                        <div className="min-w-0">
                          <h4 className="font-bold text-gray-900 text-base leading-tight truncate">
                            {member.name}
                          </h4>
                          <span className="inline-block bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded font-semibold mt-1">
                            {member.position}
                          </span>
                          <p className="text-xs text-gray-500 mt-2">
                            <strong>Edu:</strong> {member.education || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Exp:</strong> {member.experience || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <p className="text-xs text-gray-600 mt-4 line-clamp-3 leading-relaxed border-t border-gray-100 pt-3">
                        {member.description}
                      </p>

                      <div className="flex items-center space-x-2 mt-4 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => handleOpenEditTeam(member)}
                          className="flex-1 py-1.5 px-3 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded text-xs font-semibold flex items-center justify-center"
                        >
                          <Edit2 size={13} className="mr-1.5" />
                          Edit Details
                        </button>
                        <button
                          onClick={() => handleDeleteTeamMember(member.id, member.name)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded border border-red-200"
                          title="Delete Member"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: CONTACT FORM INQUIRIES */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-gray-200 gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Contact Inquiries</h2>
                <p className="text-sm text-gray-500">Messages sent via the website Contact page.</p>
              </div>
              <button
                onClick={fetchContacts}
                className="text-xs font-medium text-purple-600 hover:text-purple-700 flex items-center"
              >
                <RefreshCw size={14} className="mr-1" />
                Refresh
              </button>
            </div>

            {contacts.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <Mail size={36} className="mx-auto text-gray-300 mb-2" />
                <p>No messages received yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-xs uppercase border-b">
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Name</th>
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Phone</th>
                      <th className="py-3 px-4">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contacts.map((msg, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4 font-semibold text-gray-900">{msg.name}</td>
                        <td className="py-3 px-4 text-purple-600">
                          <a href={`mailto:${msg.email}`}>{msg.email}</a>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          <a href={`tel:${msg.phone}`}>{msg.phone}</a>
                        </td>
                        <td className="py-3 px-4 text-gray-700 max-w-md">{msg.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: DONATIONS */}
        {activeTab === 'donations' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-gray-200 gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Donation Pledges & Leads</h2>
                <p className="text-sm text-gray-500">Donations logged through the Donate page form.</p>
              </div>
              <button
                onClick={fetchDonations}
                className="text-xs font-medium text-purple-600 hover:text-purple-700 flex items-center"
              >
                <RefreshCw size={14} className="mr-1" />
                Refresh
              </button>
            </div>

            {donations.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <DollarSign size={36} className="mx-auto text-gray-300 mb-2" />
                <p>No donation pledges recorded yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-xs uppercase border-b">
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Donor Name</th>
                      <th className="py-3 px-4">Amount</th>
                      <th className="py-3 px-4">Contact</th>
                      <th className="py-3 px-4">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {donations.map((d, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">
                          {d.timestamp ? new Date(d.timestamp).toLocaleString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4 font-semibold text-gray-900">{d.donor_name}</td>
                        <td className="py-3 px-4 text-green-700 font-bold text-base">
                          ₹{Number(d.amount).toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-600">
                          <div>{d.email}</div>
                          <div>{d.phone}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-700 max-w-md">{d.message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* MODAL: TEAM MEMBER ADD / EDIT */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowTeamModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>

            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
            </h3>

            {teamModalError && (
              <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs flex items-center">
                <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                <span>{teamModalError}</span>
              </div>
            )}

            <form onSubmit={handleSaveTeamMember} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                  placeholder="e.g. Ms. Maniza Begum"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Position / Designation *</label>
                <input
                  type="text"
                  required
                  value={memberForm.position}
                  onChange={(e) => setMemberForm({ ...memberForm, position: e.target.value })}
                  placeholder="e.g. Vice Chairperson, or Director"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Education (Optional)</label>
                  <input
                    type="text"
                    value={memberForm.education}
                    onChange={(e) => setMemberForm({ ...memberForm, education: e.target.value })}
                    placeholder="e.g. B.Tech / MBA"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Experience (Optional)</label>
                  <input
                    type="text"
                    value={memberForm.experience}
                    onChange={(e) => setMemberForm({ ...memberForm, experience: e.target.value })}
                    placeholder="e.g. 5+ Years"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Photo URL / Select Image</label>
                <div className="space-y-2">
                  <select
                    value={memberForm.image}
                    onChange={(e) => setMemberForm({ ...memberForm, image: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-xs bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    <option value="">-- Choose from Uploaded Media --</option>
                    {images.map((img) => (
                      <option key={img.filename} value={img.url}>
                        {img.filename} ({img.category})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={memberForm.image}
                    onChange={(e) => setMemberForm({ ...memberForm, image: e.target.value })}
                    placeholder="/images/gallery3.jpeg"
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  Tip: Pick an image above or paste any custom image path.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Biography / Description</label>
                <textarea
                  rows="3"
                  value={memberForm.description}
                  onChange={(e) => setMemberForm({ ...memberForm, description: e.target.value })}
                  placeholder="Short description of member background and contribution..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                />
              </div>

              <div className="flex space-x-3 pt-4 border-t">
                <button
                  type="button"
                  disabled={teamModalLoading}
                  onClick={() => setShowTeamModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={teamModalLoading}
                  className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg text-sm shadow flex items-center justify-center"
                >
                  {teamModalLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin mr-2" />
                      Saving Member...
                    </>
                  ) : (
                    editingMember ? 'Save Changes' : 'Add Member'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: FULL IMAGE PREVIEW */}
      {previewModalImg && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewModalImg(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewModalImg(null)}
              className="absolute top-4 right-4 bg-white text-gray-900 p-2 rounded-full shadow hover:bg-gray-100 z-10"
            >
              <X size={20} />
            </button>
            <img
              src={previewModalImg}
              alt="Preview"
              className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl mx-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
